import { z } from 'zod';
import { coercedArray, coercedBoolean } from '../_shared/coerce.js';
const BOOKING_SOURCES = ['AFFIDIUM_IMPORT', 'CHECKOUT', 'RESERVATION'];
const BOOKING_STATUSES = [
    'AWAITING_DEPOSIT',
    'CANCELLED',
    'DEPOSIT_PAID',
    'FULLY_PAID_PENDING',
    'FULLY_PAID_TRAVELED',
];
const INSURANCE_DEVIS_STATUSES = [
    'CONTRACT_CANCELLED',
    'CONTRACT_CONFIRMED',
    'CONTRACT_FAILED',
    'NONE',
    'QUOTE_CREATED',
    'QUOTE_FAILED',
    'QUOTE_PENDING',
];
const TRAVEL_CLASSES = ['BUSINESS', 'ECONOMY', 'PREMIUM'];
/**
 * Colonnes triables de l'onglet « Dossiers ». Les identifiants sont ceux des
 * colonnes du tableau, pour que le clic sur un en-tête se traduise sans table
 * de correspondance côté front.
 *
 * Trois d'entre elles — reste à payer, marge vol, marge prestations — ne sont
 * pas des champs de la base mais des différences calculées, que Prisma ne sait
 * pas ordonner sans SQL brut. Le service les trie en mémoire, sur le même
 * chemin que les filtres post-requête.
 */
export const STATS_BO_SORT_KEYS = [
    'amadeusPriceCents',
    'client',
    'departureDate',
    'extrasMargin',
    'margin',
    'passengerCount',
    'pnr',
    'reference',
    'remainingCents',
    'source',
    'status',
    'totalPriceCents',
];
const hourField = z.coerce.number().int().min(0).max(23).optional();
const nonNegativeInt = z.coerce.number().int().min(0).optional();
export const statsBoQuerySchema = z.object({
    clientFullName: z.string().optional(),
    passengerFullName: z.string().optional(),
    accountCodes: coercedArray(z.string()),
    airlineIataCodes: coercedArray(z.string()),
    arrivalLocations: coercedArray(z.string()),
    createdFrom: z.string().optional(),
    createdHourMax: hourField,
    createdHourMin: hourField,
    createdTo: z.string().optional(),
    departureFrom: z.string().optional(),
    departureLocations: coercedArray(z.string()),
    departureTo: z.string().optional(),
    hasAccountCode: z.enum(['with', 'without']).optional(),
    hasPromo: z.enum(['with', 'without']).optional(),
    insuranceStatuses: coercedArray(z.enum(INSURANCE_DEVIS_STATUSES)),
    page: nonNegativeInt,
    pageSize: z.coerce.number().int().min(1).max(200).optional(),
    passengerCountMax: nonNegativeInt,
    passengerCountMin: nonNegativeInt,
    priceMaxCents: nonNegativeInt,
    priceMinCents: nonNegativeInt,
    promoCodes: coercedArray(z.string()),
    reference: z.string().optional(),
    sortBy: z.enum(STATS_BO_SORT_KEYS).optional(),
    sortDir: z.enum(['asc', 'desc']).optional(),
    sources: coercedArray(z.enum(BOOKING_SOURCES)),
    statuses: coercedArray(z.enum(BOOKING_STATUSES)),
    travelClasses: coercedArray(z.enum(TRAVEL_CLASSES)),
    travelType: z.enum(['FLIGHT', 'TOURISM']).optional(),
    tripType: z.enum(['one-way', 'round-trip']).optional(),
    /**
     * Dossiers neutres : « true » ne garde qu'eux, « false » les exclut, absent
     * les laisse se fondre dans les totaux — ce qui est le comportement voulu par
     * défaut, ces ventes comptent comme les autres.
     */
    untracked: coercedBoolean(),
    withDeposit: coercedBoolean(),
});
/**
 * Filtres de l'onglet collaborateurs. Volontairement réduits à la période et à
 * l'agence : filtrer une productivité d'agent par aéroport de départ produirait
 * un chiffre que personne ne saurait interpréter.
 */
export const statsBoCollaboratorsQuerySchema = z.object({
    agency: z.enum(['ALL', 'MRS', 'RUN']).optional(),
    createdFrom: z.string().optional(),
    createdTo: z.string().optional(),
    /** Grain des séries d'évolution. Journalier par défaut ; l'horaire sert la
     *  période « Jour » du front et n'est honoré que sur une fenêtre courte. */
    granularity: z.enum(['day', 'hour']).optional(),
});
